import { type CSSProperties, useMemo } from 'react';
import { css, type Theme } from '@emotion/react';
import { CaretCircleDown, XCircle } from '@phosphor-icons/react';

interface CollapseIconProps {
  open: boolean;
  size?: number;
  weight?: 'bold' | 'regular';
  hardCloseIcon?: boolean;
}

/**
 * Render the "open" & "close" icons of a collapsible component, like an accordion, depending on the open state.
 */
export const CollapseIcon = ({ open, size = 24, weight = 'bold', hardCloseIcon = false }: CollapseIconProps) => {
  const CloseIcon = useMemo(() => (hardCloseIcon ? XCircle : CaretCircleDown), [hardCloseIcon]);

  return (
    <span className="icon" style={{ '--size': `${size}px` } as CSSProperties} css={collapseIconStyle}>
      <CaretCircleDown size={size} weight={weight} style={{ transform: `rotate(${open ? 0.5 : 0}turn)`, opacity: open ? 0 : undefined }} />
      <CloseIcon
        size={size}
        weight="fill"
        style={{ transform: `scale(1.055) rotate(${open ? 0.5 : 0}turn)`, opacity: open ? undefined : 0 }}
      />
    </span>
  );
};

const collapseIconStyle = (theme: Theme) => css`
  display: inline-block;
  position: relative;
  width: var(--size);
  height: var(--size);

  > svg {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    color: ${theme.color.grey[100]};
    opacity: 0.6;

    transition:
      opacity 400ms ease,
      transform 400ms ${theme.easing.springy};
  }

  // The "close" icon being dark makes it look ever so slightly smaller than the "open" icon. We compensate for that here.
  > svg:last-of-type {
    transform: scale(1.055);
  }
`;
